/**
 * @file care-variable.schema.ts
 * @description Catálogo de variáveis do builder de cuidado.
 *
 * Duas origens, com naturezas diferentes:
 *
 *   BLOCK  — criada pela equipe clínica, gravada por uma resposta. Tem valor
 *            persistido em `userCareStates.variables`.
 *
 *   SYSTEM — nativa. NÃO tem valor armazenado: cada uma tem um resolver na API
 *            que a computa na hora, a partir da fonte real (gestação, cadastro,
 *            prontuário). Sem cópia não há dado velho — `idade_gestacional`
 *            avança sozinha toda semana, `risco_gestacional` reflete o que o
 *            profissional acabou de editar.
 *
 * Nativas são somente-leitura no admin (badge "Sistema"): não podem ser
 * renomeadas, apagadas, nem alvo de `SET_VARIABLE`. Decisão de 29/08/2026 —
 * prontuário é fonte, não destino; escrita de volta entra depois como ação
 * mapeada explícita e validada com o time clínico.
 */

import { z } from 'zod';

export const CareVariableDataTypeEnum = z.enum([
  'BOOLEAN',
  'TEXT',
  'NUMBER',
  'DATE',
  'SELECT',
  'LIST',
]);
export type CareVariableDataType = z.infer<typeof CareVariableDataTypeEnum>;

export const CareVariableOriginEnum = z.enum(['BLOCK', 'SYSTEM']);
export type CareVariableOrigin = z.infer<typeof CareVariableOriginEnum>;

/** `USER` persiste no perfil; `SESSION` vive só durante a resposta. */
export const CareVariableScopeEnum = z.enum(['USER', 'SESSION']);
export type CareVariableScope = z.infer<typeof CareVariableScopeEnum>;

/**
 * Variável derivada. `SUM` soma o `scoreValue` das opções escolhidas nos blocos
 * listados — é como PHQ-9, IRSAG e EPDS viram configuração em vez de código.
 */
export const CareVariableCalculationSchema = z.object({
  type: z.literal('SUM'),
  /** Blocos que compõem a soma. Vazio = todos os blocos pontuados do item. */
  blockIds: z.array(z.string()).default([]),
  /**
   * Item ao qual o cálculo pertence.
   *
   * Escore é intrinsecamente do instrumento: `phq9_total` só faz sentido dentro
   * do PHQ-9. Sem este escopo, responder o IRSAG também gravaria `phq9_total`
   * com a soma do IRSAG — dois instrumentos diferentes escrevendo na mesma
   * variável, e a faixa clínica avaliada sobre o número errado.
   *
   * `null` = aplica a qualquer item (comportamento legado, mantido para não
   * quebrar documento gravado antes deste campo existir).
   */
  itemId: z.string().nullable().default(null),
});
export type CareVariableCalculation = z.infer<typeof CareVariableCalculationSchema>;

export const CareVariableSchema = z.object({
  id: z.string(),
  /**
   * Chave única, snake_case MINÚSCULO. Ex.: `tem_dmg`, `padrao_alimentar`.
   *
   * Só minúsculas de propósito: o nome é comparado como string em toda regra
   * publicada, e `evaluateCondition` não normaliza caixa. Permitir as duas
   * formas deixaria `tem_DMG` e `tem_dmg` conviverem como variáveis distintas —
   * o tipo de divergência que só aparece quando uma regra silenciosamente
   * nunca casa.
   *
   * A normalização acontece antes daqui (serviço e modal do admin); esta regex
   * é a última barreira, na fronteira do armazenamento.
   */
  name: z
    .string()
    .regex(
      /^[a-z][a-z0-9_]*$/,
      'Use snake_case minúsculo: comece com letra e utilize apenas a–z, 0–9 e _'
    ),
  label: z.string(),
  description: z.string().nullable().optional(),
  dataType: CareVariableDataTypeEnum,
  origin: CareVariableOriginEnum.default('BLOCK'),
  scope: CareVariableScopeEnum.default('USER'),
  calculation: CareVariableCalculationSchema.nullable().optional(),
  /** Valores possíveis, quando `dataType` é SELECT. Usado pelo editor de regras. */
  allowedValues: z.array(z.string()).default([]),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().default(null),
});

export type CareVariable = z.infer<typeof CareVariableSchema>;

// ---------------------------------------------------------------------------
// Variáveis nativas — o seed do catálogo
// ---------------------------------------------------------------------------

/**
 * Definição de uma nativa. Fica no pacote de schemas (e não só na API) porque
 * o admin precisa listar, descrever e oferecer nos editores de regra sem
 * depender de round-trip.
 *
 * `sourceHint` é texto para a coluna "Gravada por" do catálogo — a mesma coluna
 * que o mockup da equipe já mostrava com "Sistema (automático)".
 */
export interface NativeCareVariableDefinition {
  name: string;
  label: string;
  description: string;
  dataType: CareVariableDataType;
  sourceHint: string;
  allowedValues?: string[];
}

/**
 * Catálogo nativo da fase 1, ancorado em campos que EXISTEM hoje em
 * `PregnancySchema` e `UserSchema`. Cada entrada tem um resolver correspondente
 * em `packages/api/src/services/care/native-variables.ts` — a lista e os
 * resolvers são mantidos em sincronia por um teste que falha se divergirem.
 */
export const NATIVE_CARE_VARIABLES: NativeCareVariableDefinition[] = [
  // --- Jornada e identidade ---
  {
    name: 'papel',
    label: 'Papel na jornada',
    description:
      'Se a pessoa é a titular da linha de cuidado ou acompanha alguém que está nela. ' +
      'Figura paterna é sempre acompanhante.',
    dataType: 'SELECT',
    sourceHint: 'Cadastro · user.userType',
  },
  {
    name: 'jornada',
    label: 'Jornada atual',
    description:
      'Fase da usuária, derivada do tipo de perfil e do status da gestação. ' +
      'Para acompanhante, é a fase de quem ela acompanha.',
    dataType: 'SELECT',
    sourceHint: 'Cadastro · user.userType + pregnancy.status',
    allowedValues: ['TENTANTE', 'PREGNANT', 'POSTPARTUM', 'MAE_DE_ANJO', 'ADOTANTE', 'MAE', 'FATHER_FIGURE'],
  },
  {
    name: 'idade',
    label: 'Idade da usuária',
    description:
      'Idade em anos completos. Pode ser desconhecida — regras por faixa etária devem prever o ramo "não informado".',
    dataType: 'NUMBER',
    sourceHint: 'Cadastro · user.dateOfBirth',
  },
  {
    name: 'primeira_gestacao',
    label: 'Primeira gestação',
    description: 'Verdadeiro quando a usuária não possui gestações anteriores registradas.',
    dataType: 'BOOLEAN',
    sourceHint: 'Sistema · histórico de gestações',
  },

  // --- Tempo gestacional ---
  {
    name: 'idade_gestacional_semanas',
    label: 'Idade gestacional (semanas)',
    description:
      'Semanas completas. Precedência: idade gestacional aferida por profissional; senão, DUM.',
    dataType: 'NUMBER',
    sourceHint: 'Gestação · enforcedGestationalAge → DUM',
  },
  {
    name: 'idade_gestacional_dias',
    label: 'Idade gestacional (dias totais)',
    description: 'Total de dias de gestação, para regras que precisam de granularidade fina.',
    dataType: 'NUMBER',
    sourceHint: 'Gestação · enforcedGestationalAge → DUM',
  },
  {
    name: 'trimestre',
    label: 'Trimestre',
    description: '1 (até 13 sem), 2 (14–27) ou 3 (28+). Derivado da idade gestacional.',
    dataType: 'NUMBER',
    sourceHint: 'Gestação · derivado',
  },
  {
    name: 'dpp',
    label: 'Data provável do parto',
    description: 'DPP registrada ou calculada a partir da idade gestacional.',
    dataType: 'DATE',
    sourceHint: 'Gestação · dueDate',
  },
  {
    name: 'data_parto',
    label: 'Data do parto',
    description: 'Preenchida no puerpério. Ausente enquanto a gestação está ativa.',
    dataType: 'DATE',
    sourceHint: 'Gestação · childbirthDate',
  },
  {
    name: 'idade_bebe_dias',
    label: 'Idade do bebê (dias)',
    description: 'Dias desde o parto. Base das janelas do puerpério e do calendário vacinal.',
    dataType: 'NUMBER',
    sourceHint: 'Gestação · childbirthDate',
  },

  // --- Prontuário clínico ---
  {
    name: 'risco_gestacional',
    label: 'Classificação de risco',
    description: 'Classificação registrada pelo profissional no prontuário.',
    dataType: 'SELECT',
    sourceHint: 'Prontuário · clinicalBaseline.riskClassification',
    allowedValues: ['LOW', 'HIGH', 'HABITUAL'],
  },
  {
    name: 'fatores_risco',
    label: 'Fatores de risco (códigos)',
    description:
      'Lista de códigos de fator de risco. É a âncora recomendada para ativação por contexto — códigos, não texto livre.',
    dataType: 'LIST',
    sourceHint: 'Prontuário · clinicalBaseline.riskFactors[].code',
  },
  {
    name: 'comorbidades',
    label: 'Comorbidades',
    description: 'Lista livre registrada no prontuário. Para regra dura, prefira fatores_risco.',
    dataType: 'LIST',
    sourceHint: 'Prontuário · clinicalBaseline.comorbidities',
  },
  {
    name: 'alergias',
    label: 'Alergias',
    description: 'Lista livre registrada no prontuário.',
    dataType: 'LIST',
    sourceHint: 'Prontuário · clinicalBaseline.allergies',
  },
  {
    name: 'habitos',
    label: 'Hábitos',
    description: 'Lista livre registrada no prontuário.',
    dataType: 'LIST',
    sourceHint: 'Prontuário · clinicalBaseline.habits',
  },
  {
    name: 'tipo_sanguineo',
    label: 'Tipo sanguíneo',
    description: 'Relevante para regras como Coombs indireto em Rh negativo.',
    dataType: 'SELECT',
    sourceHint: 'Prontuário · clinicalBaseline.bloodType',
    allowedValues: ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'UNKNOWN'],
  },
  {
    name: 'altura_cm',
    label: 'Altura (cm)',
    description: 'Altura registrada no prontuário.',
    dataType: 'NUMBER',
    sourceHint: 'Prontuário · clinicalBaseline.heightCm',
  },
  {
    name: 'peso_pre_gestacional',
    label: 'Peso pré-gestacional (kg)',
    description: 'Base do IMC inicial e da curva de ganho de peso.',
    dataType: 'NUMBER',
    sourceHint: 'Prontuário · clinicalBaseline.prePregnancyWeightKg',
  },

  // --- Medições ---
  {
    name: 'ultima_pressao_sistolica',
    label: 'Última pressão sistólica',
    description: 'Valor mais recente registrado. Ausente se nunca houve registro.',
    dataType: 'NUMBER',
    sourceHint: 'Medições · blood_pressure',
  },
  {
    name: 'ultimo_peso',
    label: 'Último peso registrado (kg)',
    description: 'Valor mais recente da série de peso.',
    dataType: 'NUMBER',
    sourceHint: 'Medições · weight_bmi',
  },
  {
    name: 'ultima_glicemia_jejum',
    label: 'Última glicemia de jejum',
    description: 'Valor mais recente. Meta gestacional habitual: abaixo de 95 mg/dL.',
    dataType: 'NUMBER',
    sourceHint: 'Medições · glucose_fasting',
  },

  // --- Bebê e parto ---
  {
    name: 'nome_bebe',
    label: 'Nome do bebê',
    description:
      'Para interpolação em texto. Quando ausente, o app usa "o bebê" — requisito da jornada puérpera.',
    dataType: 'TEXT',
    sourceHint: 'Gestação · babies[0].name',
  },
  {
    name: 'sexo_bebe',
    label: 'Sexo do bebê',
    description: 'Registrado na gestação.',
    dataType: 'SELECT',
    sourceHint: 'Gestação · babies[0].sex',
    allowedValues: ['MALE', 'FEMALE', 'UNKNOWN'],
  },
  {
    name: 'quantidade_bebes',
    label: 'Quantidade de bebês',
    description: 'Um para gestação única, dois ou mais para múltiplas.',
    dataType: 'NUMBER',
    sourceHint: 'Gestação · babies.length',
  },
  {
    name: 'tipo_parto_planejado',
    label: 'Via de parto planejada',
    description: 'Preferência registrada no plano de parto.',
    dataType: 'SELECT',
    sourceHint: 'Plano de parto · birthPlan.deliveryMode',
    allowedValues: ['NORMAL', 'NATURAL', 'CESAREAN'],
  },
  {
    name: 'loss_care_point',
    label: 'Momento do cuidado na perda',
    description: 'Único dado da jornada Perdi meu bebê coletado hoje; entra pelo profissional.',
    dataType: 'SELECT',
    sourceHint: 'Gestação · lossCarePoint',
  },
];

/** Índice por nome, para lookup O(1) no motor e no admin. */
export const NATIVE_CARE_VARIABLES_BY_NAME: Record<string, NativeCareVariableDefinition> =
  NATIVE_CARE_VARIABLES.reduce((acc, v) => {
    acc[v.name] = v;
    return acc;
  }, {} as Record<string, NativeCareVariableDefinition>);

export function isNativeCareVariable(name: string): boolean {
  return Object.prototype.hasOwnProperty.call(NATIVE_CARE_VARIABLES_BY_NAME, name);
}

/**
 * Pseudo-variáveis resolvidas pelo motor a partir do histórico de respostas,
 * e não de um campo. Ficam fora de NATIVE_CARE_VARIABLES porque recebem
 * argumento: `respondeu(itemId)` e `resposta(itemId, blockId)`.
 */
export const CARE_PSEUDO_VARIABLES = ['respondeu', 'resposta'] as const;

/** `respondeu(abc)` → { fn: 'respondeu', args: ['abc'] }. */
export function parsePseudoVariable(
  expression: string
): { fn: string; args: string[] } | null {
  const match = expression.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\(([^)]*)\)$/);
  if (!match) return null;
  const [, fn, rawArgs] = match;
  if (!(CARE_PSEUDO_VARIABLES as readonly string[]).includes(fn)) return null;
  const args = rawArgs
    .split(',')
    .map((a) => a.trim().replace(/^['"]|['"]$/g, ''))
    .filter(Boolean);
  return { fn, args };
}
